import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import type { OperationDefinition } from '@/types/operations';
import type { MCPServerConfig } from '@/types/mcpTypes';
import type { CallableWorkflow } from '@/hooks/useCallableWorkflows';
import type { InsertionPoint } from '@/stores/workflowStore';
import { PanelResizeHandle, usePanelWidth } from '@/components/shared/PanelResizeHandle';
import { OperationPickerPanel } from './OperationPickerPanel';
import { AgentMcpView } from './AgentMcpView';
import { AgentSubworkflowView } from './AgentSubworkflowView';
import { AgentTypeView, type AgentSelection } from './AgentTypeView';
import { useOperationPickerPanelStyles } from './OperationPickerPanel.styles';

/**
 * Discriminated union describing which picker is currently visible.
 *
 * The host is the single mount point for every "add something to the workflow"
 * panel, so switching views (e.g. operations -> agent-type) swaps the body
 * in-place rather than stacking another panel on top.
 *
 * The `operations` variant carries the data `handleOperationSelect` needs
 * (insertionPoint, mode, targetAgentName) so the existing add-action / add-
 * trigger / add-tool flows keep working without rewiring.
 */
export type PickerView =
  | {
      kind: 'operations';
      mode: 'trigger' | 'action' | 'tool';
      insertionPoint: InsertionPoint | null;
      targetAgentName?: string;
      title?: string;
    }
  | { kind: 'mcp'; targetAgentName: string | null; workflowName: string }
  | { kind: 'subworkflow'; targetAgentName: string | null; workflowName: string }
  | {
      kind: 'agent-type';
      insertionPoint: { sourceNodeId: string; targetNodeId?: string } | null;
    };

export interface PickerPanelHostProps {
  view: PickerView | null;
  onOperationSelect: (operation: OperationDefinition) => void;
  /**
   * Called when the user picks an MCP server card. The whole server is
   * attached as a tool; the user later narrows `allowedTools` from the
   * node-editor's MCP config tab. `source` distinguishes managed (control-
   * plane apiOperations) vs. BYO (agentMcpConnections in connections.json) so
   * the caller can route the persistence path appropriately.
   */
  onMcpServerSelect: (server: MCPServerConfig, source: 'managed' | 'byo') => void;
  onSubworkflowSelect: (workflow: CallableWorkflow) => void;
  onAgentTypeSelect: (selection: AgentSelection) => void;
  onClose: () => void;
}

const useStyles = makeStyles({
  body: {
    paddingInline: tokens.spacingHorizontalL,
  },
});

/**
 * Single picker panel host. Renders the operations picker, MCP tool picker,
 * subworkflow picker, or agent-type picker based on `view`. The shared panel
 * chrome stays mounted across MCP / subworkflow / agent-type transitions so
 * the body swaps in-place instead of two panels stacking.
 */
export const PickerPanelHost = ({
  view,
  onOperationSelect,
  onMcpServerSelect,
  onSubworkflowSelect,
  onAgentTypeSelect,
  onClose,
}: PickerPanelHostProps) => {
  const panelStyles = useOperationPickerPanelStyles();
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  // Reset the search filter whenever the picker closes or swaps to a different
  // view kind, otherwise a stale query from the previous open carries over
  // and silently filters the new list (e.g. open MCP, type "foo", close,
  // reopen subworkflow picker and find it unexpectedly filtered).
  useEffect(() => {
    setSearchQuery('');
  }, [view?.kind]);
  // Share width state with OperationPickerPanel via the same `panelId`, so
  // resizing any picker view resizes them all and the size is persisted.
  const {
    width: panelWidth,
    setWidth: setPanelWidth,
    resetWidth: resetPanelWidth,
  } = usePanelWidth({
    panelId: 'operation-picker',
    defaultWidth: 480,
    minWidth: 400,
    maxWidth: 720,
  });

  if (!view) return null;

  if (view.kind === 'operations') {
    // OperationPickerPanel owns its own resizable chrome and category UI.
    const mode = view.mode === 'tool' ? 'action' : view.mode;
    return (
      <OperationPickerPanel
        mode={mode}
        onOperationSelect={onOperationSelect}
        onClose={onClose}
        {...(view.title && { title: view.title })}
      />
    );
  }

  // Auxiliary views share a compact panel chrome.
  let titleText: string;
  let ariaLabel: string;
  let body: React.ReactNode;
  let searchPlaceholder: string | null = null;

  switch (view.kind) {
    case 'mcp':
      titleText = 'MCP Tools';
      ariaLabel = 'MCP tool picker';
      searchPlaceholder = 'Search MCP servers...';
      body = (
        <AgentMcpView
          searchQuery={searchQuery}
          workflowName={view.workflowName}
          onServerSelect={onMcpServerSelect}
        />
      );
      break;
    case 'subworkflow':
      titleText = 'Sub-workflow';
      ariaLabel = 'Sub-workflow picker';
      searchPlaceholder = 'Search workflows...';
      body = (
        <AgentSubworkflowView
          currentWorkflowName={view.workflowName}
          searchQuery={searchQuery}
          onWorkflowSelect={onSubworkflowSelect}
        />
      );
      break;
    case 'agent-type':
      titleText = 'Add an agent';
      ariaLabel = 'Add agent';
      body = <AgentTypeView onCancel={onClose} onSubmit={onAgentTypeSelect} />;
      break;
  }

  return (
    <div
      className={panelStyles.panel}
      role="region"
      aria-label={ariaLabel}
      style={{ width: panelWidth }}
    >
      <PanelResizeHandle
        edge="left"
        value={panelWidth}
        min={400}
        max={720}
        defaultValue={480}
        ariaLabel="Resize picker panel"
        collapsible={false}
        onResize={setPanelWidth}
        onReset={resetPanelWidth}
      />
      <div className={panelStyles.header}>
        <Text as="h2" className={panelStyles.headerTitle}>
          {titleText}
        </Text>
        <Tooltip content="Close (Esc)" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={onClose}
            aria-label="Close panel"
          />
        </Tooltip>
      </div>
      {searchPlaceholder !== null && (
        <div className={panelStyles.searchContainer}>
          <Input
            className={panelStyles.searchInput}
            contentBefore={<SearchRegular aria-hidden="true" />}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={searchPlaceholder}
          />
        </div>
      )}
      <div className={panelStyles.content}>
        <div className={styles.body}>{body}</div>
      </div>
    </div>
  );
};
